// src/components/Admin/FirmPortalClients.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Paper, Table, TableContainer, TableHead, TableRow, TableCell,
    TableBody, TableSortLabel, CircularProgress, Alert, Chip, TextField,
    FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Button,
    Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment, Grid,
    Checkbox, Toolbar, Menu
} from '@mui/material';
import {
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    QuestionAnswer as QuestionAnswerIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    AccessTime as AccessTimeIcon,
    Warning as WarningIcon,
    PlaylistAdd as BulkIcon,
    SwapHoriz as ReassignIcon,
    ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import FirmPortalClientDetail from './FirmPortalClientDetail';
import { IndustryFilterSelect } from '../common/IndustrySelect';

   const FirmPortalClients = ({ user, orgData, orgConfig }) => {
    // Data state
    const [clients, setClients] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFcfoId, setSelectedFcfoId] = useState('ALL');
    const [selectedIndustry, setSelectedIndustry] = useState('ALL');
    const [selectedStatus, setSelectedStatus] = useState('ALL');
    
    // Sort state
    const [orderByField, setOrderByField] = useState('lastActivityAt');
    const [orderDirection, setOrderDirection] = useState('desc');
    
    // Detail view state
    const [selectedClient, setSelectedClient] = useState(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Bulk selection state
    const [selectedClientIds, setSelectedClientIds] = useState(new Set());
    const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);

    // Bulk Ask Question modal state
    const [bulkQuestionOpen, setBulkQuestionOpen] = useState(false);
    const [bulkQuestionText, setBulkQuestionText] = useState('');
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    // Reassign modal state
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignNewFcfoId, setReassignNewFcfoId] = useState('');
    const [reassignHistory, setReassignHistory] = useState(false);
    const [reassignSubmitting, setReassignSubmitting] = useState(false);

      // Check if user is admin or enterprise user (both can perform actions)
    const isAdmin = user?.firmPortalRole === 'admin';
    const isEnterprise = user?.firmPortalRole === 'admin' || user?.firmPortalRole === 'user';
    
    const fcfoLabel = orgConfig?.terminology?.fcfo || 'Advisor';
    const fcfosLabel = orgConfig?.terminology?.fcfos || 'Advisors';

    // Max clients for bulk questions
    const MAX_BULK_CLIENTS = 6;

    // Inactive threshold (default 30 days)
    const inactiveThresholdDays = orgData?.inactiveClientThresholdDays || 30;

    // Fetch clients and members
    useEffect(() => {
        const fetchData = async () => {
            if (!user?.organizationId) {
                setError("No organization ID found.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Fetch all members (FCFOs) in the organization
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const membersSnap = await getDocs(membersQuery);
                const memberList = membersSnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                // Filter to only show users who have user portal access (actual Advisors)
                const fcfoMembers = memberList.filter(m => m.userPortalAccess !== false);
                setMembers(fcfoMembers);

                // Fetch all clients in the organization
                const clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', user.organizationId)
                );
                const clientsSnap = await getDocs(clientsQuery);
                
                // Filter to only show Active and Inactive clients
// Excludes: 'Setup', 'Pending Admin Review' (managed in Savvai Admin)
const clientList = clientsSnap.docs
    .filter(doc => ['Active', 'Inactive'].includes(doc.data().status))
    .map(doc => {
                    const data = doc.data();
                    const fcfo = fcfoMembers.find(m => m.id === data.fcfoId);
                    
                    // Calculate inactive status
                    const lastActivity = data.lastActivityAt?.toDate() || data.createdAt?.toDate();
                    const daysSinceActivity = lastActivity 
                        ? Math.floor((new Date() - lastActivity) / (1000 * 60 * 60 * 24))
                        : null;
                    const isInactive = daysSinceActivity !== null && daysSinceActivity > inactiveThresholdDays;
                    
                    return {
                        id: doc.id,
                        ...data,
                        fcfoName: fcfo?.name || fcfo?.email || 'Unassigned',
                        fcfoEmail: fcfo?.email || '',
                        daysSinceActivity,
                        isInactive
                    };
                });

                setClients(clientList);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching clients:', err);
                setError(`Failed to load clients: ${err.message}`);
                setLoading(false);
            }
        };

        fetchData();
    }, [user?.organizationId, inactiveThresholdDays]);

    // Filter and sort clients
    const filteredClients = useMemo(() => {
        let result = [...clients];

        // Apply search filter
        if (searchQuery.trim()) {
            const search = searchQuery.toLowerCase();
            result = result.filter(client =>
                client.name?.toLowerCase().includes(search) ||
                client.officialCompanyName?.toLowerCase().includes(search) ||
                client.fcfoName?.toLowerCase().includes(search) ||
                client.industry?.toLowerCase().includes(search)
            );
        }

        // Apply FCFO filter
        if (selectedFcfoId !== 'ALL') {
            result = result.filter(client => client.fcfoId === selectedFcfoId);
        }

        // Apply industry filter
        if (selectedIndustry !== 'ALL') {
            result = result.filter(client => client.industry === selectedIndustry);
        }

        // Apply status filter
        if (selectedStatus !== 'ALL') {
            if (selectedStatus === 'active') {
                result = result.filter(client => client.isActive !== false && !client.isInactive);
            } else if (selectedStatus === 'inactive') {
                result = result.filter(client => client.isInactive);
            } else if (selectedStatus === 'archived') {
                result = result.filter(client => client.isActive === false);
            }
        }

        // Apply sorting
        result.sort((a, b) => {
            let aVal, bVal;
            
            switch (orderByField) {
                case 'name':
                    aVal = (a.name || '').toLowerCase();
                    bVal = (b.name || '').toLowerCase();
                    break;
                case 'fcfoName':
                    aVal = (a.fcfoName || '').toLowerCase();
                    bVal = (b.fcfoName || '').toLowerCase();
                    break;
                case 'industry':
                    aVal = (a.industry || '').toLowerCase();
                    bVal = (b.industry || '').toLowerCase();
                    break;
                case 'lastActivityAt':
                    aVal = a.lastActivityAt?.toMillis() || a.createdAt?.toMillis() || 0;
                    bVal = b.lastActivityAt?.toMillis() || b.createdAt?.toMillis() || 0;
                    break;
                case 'questionCount':
                    aVal = a.questionCount || 0;
                    bVal = b.questionCount || 0;
                    break;
                default:
                    aVal = 0;
                    bVal = 0;
            }

            if (orderDirection === 'asc') {
                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            } else {
                return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
            }
        });

        return result;
    }, [clients, searchQuery, selectedFcfoId, selectedIndustry, selectedStatus, orderByField, orderDirection]);

    // Calculate summary stats
    const stats = useMemo(() => {
        const total = clients.length;
        const active = clients.filter(c => c.isActive !== false && !c.isInactive).length;
        const inactive = clients.filter(c => c.isInactive).length;
        const archived = clients.filter(c => c.isActive === false).length;
        return { total, active, inactive, archived };
    }, [clients]);

    // Handle sort change
    const handleSort = (field) => {
        if (orderByField === field) {
            setOrderDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderByField(field);
            setOrderDirection('asc');
        }
    };

    // Format date for display
    const formatDate = (timestamp) => {
        if (!timestamp?.toDate) return '—';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Handle client click
    const handleClientClick = (client) => {
        setSelectedClient(client);
        setDetailOpen(true);
    };

    // Selection handlers
     const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Limit to MAX_BULK_CLIENTS
            const idsToSelect = filteredClients.slice(0, MAX_BULK_CLIENTS).map(c => c.id);
            setSelectedClientIds(new Set(idsToSelect));
            if (filteredClients.length > MAX_BULK_CLIENTS) {
                toast.info(`Selection limited to ${MAX_BULK_CLIENTS} clients for bulk questions.`);
            }
        } else {
            setSelectedClientIds(new Set());
        }
    };

     const handleSelectClient = (clientId, event) => {
        event.stopPropagation();
        setSelectedClientIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(clientId)) {
                newSet.delete(clientId);
            } else {
                // Enforce max limit
                if (newSet.size >= MAX_BULK_CLIENTS) {
                    toast.warning(`Maximum ${MAX_BULK_CLIENTS} clients can be selected for bulk questions.`);
                    return prev; // Return unchanged
                }
                newSet.add(clientId);
            }
            return newSet;
        });
    };

    const isAllSelected = filteredClients.length > 0 && selectedClientIds.size === filteredClients.length;
    const isSomeSelected = selectedClientIds.size > 0 && selectedClientIds.size < filteredClients.length;

    // Get selected clients array
    const selectedClients = useMemo(() => {
        return clients.filter(c => selectedClientIds.has(c.id));
    }, [clients, selectedClientIds]);

    // Bulk Ask Question handler
    const handleBulkAskQuestion = async () => {
        if (!bulkQuestionText.trim()) {
            toast.error('Please enter a question.');
            return;
        }

        setBulkSubmitting(true);
        try {
            const firmPortalBulkAskQuestion = httpsCallable(functions, 'firmPortalBulkAskQuestion', { timeout: 900000 });
            const result = await firmPortalBulkAskQuestion({
                clientIds: Array.from(selectedClientIds),
                questionText: bulkQuestionText.trim()
            });

            if (result.data.success) {
                toast.success(result.data.message);
                setBulkQuestionText('');
                setBulkQuestionOpen(false);
                setSelectedClientIds(new Set());
            } else {
                throw new Error(result.data.error || 'Failed to submit questions');
            }
        } catch (err) {
            console.error('Error in bulk ask question:', err);
            toast.error(err.message || 'Failed to submit questions');
        } finally {
            setBulkSubmitting(false);
        }
    };

    // Bulk Reassign handler (single client at a time for now)
    const handleReassign = async () => {
        if (!reassignNewFcfoId) {
            toast.error('Please select a new FCFO.');
            return;
        }

        if (selectedClientIds.size !== 1) {
            toast.error('Please select exactly one client to reassign.');
            return;
        }

        setReassignSubmitting(true);
        try {
            const firmPortalReassignClient = httpsCallable(functions, 'firmPortalReassignClient', { timeout: 60000 });
            const clientId = Array.from(selectedClientIds)[0];
            
            const result = await firmPortalReassignClient({
                clientId,
                newFcfoId: reassignNewFcfoId,
                reassignHistory
            });

            if (result.data.success) {
                toast.success(result.data.message);
                setReassignOpen(false);
                setReassignNewFcfoId('');
                setReassignHistory(false);
                setSelectedClientIds(new Set());
                
                // Refresh clients
                window.location.reload(); // Simple refresh for now
            } else {
                throw new Error(result.data.error || 'Failed to reassign client');
            }
        } catch (err) {
            console.error('Error reassigning client:', err);
            toast.error(err.message || 'Failed to reassign client');
        } finally {
            setReassignSubmitting(false);
        }
    };

    // Get FCFOs for reassign dropdown (exclude current owner if single selection)
    const fcfosForReassign = useMemo(() => {
        const fcfoList = members.filter(m => m.userPortalAccess !== false && m.userType === 'FCFO');
        if (selectedClientIds.size === 1) {
            const selectedClient = clients.find(c => c.id === Array.from(selectedClientIds)[0]);
            return fcfoList.filter(f => f.id !== selectedClient?.fcfoId);
        }
        return fcfoList;
    }, [members, clients, selectedClientIds]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Client Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                View and manage all clients across your organization.
            </Typography>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{stats.total}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Clients</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">{stats.active}</Typography>
                        <Typography variant="body2" color="text.secondary">Active</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">{stats.inactive}</Typography>
                        <Typography variant="body2" color="text.secondary">Inactive</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Paper elevation={1} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="text.secondary">{stats.archived}</Typography>
                        <Typography variant="body2" color="text.secondary">Archived</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filters */}
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search clients..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>{fcfoLabel}</InputLabel>
                            <Select
                                value={selectedFcfoId}
                                label={fcfoLabel}
                                onChange={(e) => setSelectedFcfoId(e.target.value)}
                            >
                                <MenuItem value="ALL">All {fcfosLabel}</MenuItem>
                                {members.filter(m => m.userPortalAccess !== false).map(member => (
                                    <MenuItem key={member.id} value={member.id}>
                                        {member.name || member.email}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
    <IndustryFilterSelect
        value={selectedIndustry}
        onChange={setSelectedIndustry}
        organizationId={user?.organizationId}
        size="small"
        fullWidth
        label="Industry"
    />
</Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={selectedStatus}
                                label="Status"
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                <MenuItem value="ALL">All Statuses</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                                <MenuItem value="archived">Archived</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

             {/* Bulk Actions Toolbar - Show when items are selected */}
            {isEnterprise && selectedClientIds.size > 0 && (
                <Paper elevation={2} sx={{ p: 1.5, mb: 2, bgcolor: selectedClientIds.size >= MAX_BULK_CLIENTS ? 'warning.main' : 'primary.light' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" color={selectedClientIds.size >= MAX_BULK_CLIENTS ? 'warning.contrastText' : 'primary.contrastText'} fontWeight={500}>
                            {selectedClientIds.size} client{selectedClientIds.size !== 1 ? 's' : ''} selected
                            {selectedClientIds.size >= MAX_BULK_CLIENTS && ` (maximum ${MAX_BULK_CLIENTS})`}
                        </Typography>
                        <Box display="flex" gap={1}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<QuestionAnswerIcon />}
                                onClick={() => setBulkQuestionOpen(true)}
                            >
                                Ask Question ({selectedClientIds.size})
                            </Button>
                            {selectedClientIds.size === 1 && isAdmin && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<ReassignIcon />}
                                    onClick={() => setReassignOpen(true)}
                                >
                                    Reassign
                                </Button>
                            )}
                            <Button
                                size="small"
                                variant="outlined"
                                sx={{ color: 'primary.contrastText', borderColor: 'primary.contrastText' }}
                                onClick={() => setSelectedClientIds(new Set())}
                            >
                                Clear Selection
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Clients Table */}
            <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                            {isEnterprise && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={isSomeSelected}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                    />
                                </TableCell>
                            )}
                            <TableCell>
                                <TableSortLabel
                                    active={orderByField === 'name'}
                                    direction={orderByField === 'name' ? orderDirection : 'asc'}
                                    onClick={() => handleSort('name')}
                                >
                                    Client
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderByField === 'industry'}
                                    direction={orderByField === 'industry' ? orderDirection : 'asc'}
                                    onClick={() => handleSort('industry')}
                                >
                                    Industry
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderByField === 'fcfoName'}
                                    direction={orderByField === 'fcfoName' ? orderDirection : 'asc'}
                                    onClick={() => handleSort('fcfoName')}
                                >
                                    {fcfoLabel}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderByField === 'lastActivityAt'}
                                    direction={orderByField === 'lastActivityAt' ? orderDirection : 'asc'}
                                    onClick={() => handleSort('lastActivityAt')}
                                >
                                    Last Activity
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderByField === 'questionCount'}
                                    direction={orderByField === 'questionCount' ? orderDirection : 'asc'}
                                    onClick={() => handleSort('questionCount')}
                                >
                                    Questions
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredClients.length === 0 ? (
                             <TableRow>
                                <TableCell colSpan={isEnterprise ? 8 : 7} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No clients found matching your filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredClients.map((client) => (
                                <TableRow 
                                    key={client.id} 
                                    hover 
                                    sx={{ 
                                        cursor: 'pointer',
                                        bgcolor: selectedClientIds.has(client.id) 
                                            ? 'primary.lighter' 
                                            : client.isInactive ? 'warning.lighter' : 'inherit',
                                        '&:hover': { 
                                            bgcolor: selectedClientIds.has(client.id) 
                                                ? 'primary.light' 
                                                : client.isInactive ? 'warning.light' : 'action.hover' 
                                        }
                                    }}
                                     onClick={() => handleClientClick(client)}
                                >
                                    {isEnterprise && (
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedClientIds.has(client.id)}
                                                onChange={(e) => handleSelectClient(client.id, e)}
                                                disabled={!selectedClientIds.has(client.id) && selectedClientIds.size >= MAX_BULK_CLIENTS}
                                            />
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BusinessIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {client.name}
                                                </Typography>
                                                {client.officialCompanyName && client.officialCompanyName !== client.name && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {client.officialCompanyName}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
    {(client.industryCanonical || client.industry) ? (
        <Chip label={client.industryCanonical || client.industry} size="small" variant="outlined" />
    ) : (
        <Typography variant="body2" color="text.secondary">—</Typography>
    )}
</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon color="action" fontSize="small" />
                                            <Typography variant="body2">{client.fcfoName}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessTimeIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="body2">
                                                    {formatDate(client.lastActivityAt || client.createdAt)}
                                                </Typography>
                                                {client.daysSinceActivity !== null && (
                                                    <Typography variant="caption" color={client.isInactive ? 'warning.main' : 'text.secondary'}>
                                                        {client.daysSinceActivity} days ago
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            icon={<QuestionAnswerIcon />} 
                                            label={client.questionCount || 0} 
                                            size="small" 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {client.isActive === false ? (
                                            <Chip label="Archived" size="small" color="default" />
                                        ) : client.isInactive ? (
                                            <Chip 
                                                icon={<WarningIcon />} 
                                                label="Inactive" 
                                                size="small" 
                                                color="warning" 
                                            />
                                        ) : (
                                            <Chip label="Active" size="small" color="success" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="View Client Details">
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleClientClick(client)}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Client Detail Dialog */}
            <Dialog 
                open={detailOpen} 
                onClose={() => setDetailOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '90vh' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon />
                        <Typography variant="h6">{selectedClient?.name}</Typography>
                        {selectedClient?.industry && (
                            <Chip label={selectedClient.industry} size="small" variant="outlined" />
                        )}
                    </Box>
                    <Chip 
                        icon={<PersonIcon />} 
                        label={`${fcfoLabel}: ${selectedClient?.fcfoName}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                    />
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {selectedClient && (
                        <FirmPortalClientDetail 
                            client={selectedClient}
                            user={user}
                            orgConfig={orgConfig}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Ask Question Dialog */}
            <Dialog 
                open={bulkQuestionOpen} 
                onClose={() => !bulkSubmitting && setBulkQuestionOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <QuestionAnswerIcon color="primary" />
                        <span>Ask Question to {selectedClientIds.size} Client{selectedClientIds.size !== 1 ? 's' : ''}</span>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This question will be sent to all selected clients and processed individually through the answer pipeline.
                    </Alert>
                    
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Clients:</Typography>
                    <Box sx={{ mb: 2, maxHeight: 120, overflow: 'auto' }}>
                        {selectedClients.map(c => (
                            <Chip 
                                key={c.id} 
                                label={c.name} 
                                size="small" 
                                sx={{ m: 0.5 }} 
                            />
                        ))}
                    </Box>
                    
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Question"
                        placeholder="Enter your question for all selected clients..."
                        value={bulkQuestionText}
                        onChange={(e) => setBulkQuestionText(e.target.value)}
                        disabled={bulkSubmitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setBulkQuestionOpen(false)} 
                        disabled={bulkSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleBulkAskQuestion}
                        variant="contained"
                        disabled={bulkSubmitting || !bulkQuestionText.trim()}
                        startIcon={bulkSubmitting ? <CircularProgress size={20} /> : <QuestionAnswerIcon />}
                    >
                        {bulkSubmitting ? 'Submitting...' : `Submit to ${selectedClientIds.size} Clients`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reassign Client Dialog */}
            <Dialog 
                open={reassignOpen} 
                onClose={() => !reassignSubmitting && setReassignOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ReassignIcon color="secondary" />
                        <span>Reassign Client</span>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedClients.length === 1 && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Reassign <strong>{selectedClients[0]?.name}</strong> from <strong>{selectedClients[0]?.fcfoName}</strong> to a different FCFO.
                            </Alert>
                            
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>New FCFO</InputLabel>
                                <Select
                                    value={reassignNewFcfoId}
                                    label="New FCFO"
                                    onChange={(e) => setReassignNewFcfoId(e.target.value)}
                                    disabled={reassignSubmitting}
                                >
                                    {fcfosForReassign.map(fcfo => (
                                        <MenuItem key={fcfo.id} value={fcfo.id}>
                                            {fcfo.name || fcfo.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                             <Alert severity="info" sx={{ mb: 2 }}>
                                All historical questions and signals will be reassigned to the new FCFO.
                            </Alert>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setReassignOpen(false)} 
                        disabled={reassignSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleReassign}
                        variant="contained"
                        color="secondary"
                        disabled={reassignSubmitting || !reassignNewFcfoId}
                        startIcon={reassignSubmitting ? <CircularProgress size={20} /> : <ReassignIcon />}
                    >
                        {reassignSubmitting ? 'Reassigning...' : 'Reassign Client'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FirmPortalClients;